import {DatesState, YClientsFormState} from '@target-web/leadads-form-view';
import {useState, useEffect, useMemo, useCallback} from 'react';
import {fetchDates, fetchTimes} from 'utils/requests';
import {useSearchParams} from 'utils/searchParamsContext';

type useDatesProps = {
    active?: boolean;
    datetime: string;
    service_ids?: number[]; // Массив ID услуг. Если нужны даты, когда можно забронировать указанные услуги
    staff_id: YClientsFormState['staffId']; // ID сотрудника. Если нужны даты, когда можно забронировать указанного специалиста
    date?: Date; // дата в формате iso8601. Фильтр по месяцу бронирования
    date_from?: Date; // дата в формате iso8601. Начало диапазона поиска. Используется в паре с параметром "date_to" и имеет приоритет перед параметром "date".
    date_to?: Date; // дата в формате iso8601. Окончание диапазона поиска. Используется в паре с параметром "date_from" и имеет приоритет перед параметром "date".
    setDateTime: (date: string, staffId?: number) => void;
    resetDateTime: () => void;
};

export const useDates = ({
    staff_id,
    active,
    datetime,
    service_ids,
    setDateTime,
    resetDateTime,
}: useDatesProps): DatesState => {
    const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [timesIsLoading, setTimesIsLoading] = useState(true);
    const [dates, setDates] = useState<DatesState['dates']>({
        booking_dates: [],
        working_dates: [],
        booking_days: {},
        working_days: {},
    });
    const {formDataHash} = useSearchParams();

    const [times, setTimes] = useState<DatesState['times']>([]);

    const searchParams = useMemo(() => {
        const params = new URLSearchParams({lead_form_data: formDataHash});

        if (service_ids?.length) {
            service_ids.forEach((id) => params.append('service_ids[]', String(id)));
        }

        if (staff_id) {
            params.append('staff_id', staff_id.toString());
        }

        return params.toString();
    }, [service_ids, staff_id, formDataHash]);

    const timesSearchParams = useMemo(() => {
        const params = new URLSearchParams({lead_form_data: formDataHash});

        if (service_ids?.length) {
            service_ids.forEach((id) => params.append('service_ids[]', String(id)));
        }

        return params.toString();
    }, [service_ids, formDataHash]);

    const getDates = useCallback(async () => {
        setIsLoading(true);
        const r = await fetchDates(searchParams);

        if (r.data.success) {
            const datesDate = {
                ...r.data.data,
                booking_dates: r.data.data.booking_dates.sort((a, b) => (new Date(a) > new Date(b) ? 1 : -1)),
            };
            setDates(datesDate);

            if (datetime) {
                // если вдруг закончились слоты в выбранный день - скинем дату
                const curDate = new Date(datetime);
                curDate.setHours(0, 0, 0, 0);
                const awailableDays = r.data.data.booking_dates.map((d) => {
                    const dd = new Date(d);
                    dd.setHours(0, 0, 0, 0);
                    return dd.getTime();
                });
                if (!awailableDays.includes(curDate.getTime())) {
                    resetDateTime();
                }
            }
            if (!selectedDay) {
                setSelectedDay(datesDate.booking_dates[0]);
            }
            setIsLoading(false);
        }
    }, [datetime, resetDateTime, searchParams, selectedDay]);

    const getTimes = useCallback(async () => {
        if (!selectedDay) return;
        setTimesIsLoading(true);
        const staffId = staff_id ? staff_id : 0;
        const r = await fetchTimes(staffId, selectedDay, timesSearchParams);
        if (r.data.success) {
            if (datetime) {
                // если выбранный слот больше неактивен - скинем дату
                if (!r.data.data.map((d) => d.datetime).includes(datetime)) {
                    resetDateTime();
                }
            }

            setTimes(r.data.data);
            setTimesIsLoading(false);
        }
    }, [selectedDay, staff_id, timesSearchParams, datetime, resetDateTime]);

    useEffect(() => {
        if (active) {
            getDates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (active) {
            getTimes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, staff_id, timesSearchParams]);

    const onChangeSelectedDay = useCallback(
        (day: string) => {
            setDateTime('');
            setSelectedDay(day);
        },
        [setDateTime],
    );

    return {
        isLoading,
        dates,
        times,
        timesIsLoading,
        selectedDay,
        setSelectedDay: onChangeSelectedDay,
    };
};
